import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { DocumentArrowUpIcon, CloudArrowUpIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { MAX_UPLOAD_SIZE } from '../config';

const DocumentUpload = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formType, setFormType] = useState('1040');
  const [dragActive, setDragActive] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (uploading) return;
      const file = acceptedFiles[0];
      if (file.size > MAX_UPLOAD_SIZE) {
        toast.error('File too large (max 5MB)');
        return;
      }
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('formType', formType);

      api.post('/api/upload-doc', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (e) => {
          setProgress(Math.round((e.loaded * 100) / e.total));
        },
      })
        .then((response) => {
          toast.success('Document uploaded successfully');
          setUploadSuccess(true);
          setTimeout(() => setUploadSuccess(false), 2000);
          onUploadComplete();
        })
        .catch((err) => {
          console.error(err);
          toast.error(err.response?.data?.error || 'Upload failed');
        })
        .finally(() => {
          setUploading(false);
          setProgress(0);
        });
    },
    [uploading, onUploadComplete, formType]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    onDropAccepted: () => setDragActive(false),
  });

  return (
    <div className="backdrop-blur-xl bg-white/30 rounded-2xl border border-white/20 shadow-lg overflow-hidden">
      <div className="p-6 bg-gradient-to-br from-indigo-500/5 to-purple-500/5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Upload Tax Document</h3>
            <p className="text-sm text-gray-600 mt-1">Select document type and drop your file below</p>
          </div>
          <select
            id="formType"
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            className="block w-full sm:w-48 pl-3 pr-10 py-2 text-base border-0 bg-white/50 backdrop-blur-sm focus:ring-2 focus:ring-indigo-500 sm:text-sm rounded-xl"
          >
            <option value="1040">Form 1040</option>
            <option value="W2">Form W-2</option>
            <option value="1099K">Form 1099-K</option>
          </select>
        </div>

        <div
          {...getRootProps()}
          className={`relative min-h-[200px] flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
            dragActive
              ? 'border-indigo-500 bg-indigo-50/50'
              : 'border-gray-300 hover:border-indigo-400 hover:bg-white/30'
          }`}
        >
          <input {...getInputProps()} />
          <AnimatePresence mode="wait">
            {!uploading && !uploadSuccess && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col items-center"
              >
                <div className={`p-4 rounded-full bg-gradient-to-br transition-colors duration-300 ${
                  dragActive ? 'from-indigo-500/20 to-purple-500/20' : 'from-gray-500/10 to-gray-600/10'
                }`}>
                  <CloudArrowUpIcon className={`h-10 w-10 transition-colors duration-300 ${
                    dragActive ? 'text-indigo-600' : 'text-gray-600'
                  }`} />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">
                  {dragActive ? 'Drop your file here' : `Upload Form ${formType}`}
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  Drag & drop your PDF file here, or click to browse
                </p>
                <div className="flex items-center mt-4 text-xs text-gray-500">
                  <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
                  Supported format: PDF (max 5MB)
                </div>
              </motion.div>
            )}

            {uploading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center w-full max-w-xs"
              >
                <div className="w-full h-2 bg-gray-200/50 rounded-full overflow-hidden mb-4">
                  <motion.div
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <p className="text-sm font-medium text-indigo-600">Uploading... {progress}%</p>
              </motion.div>
            )}

            {uploadSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                className="flex flex-col items-center"
              >
                <div className="p-4 rounded-full bg-green-100">
                  <CheckCircleIcon className="h-10 w-10 text-green-500" />
                </div>
                <p className="text-lg font-medium text-green-600 mt-4">Upload Successful!</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload; 